import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * A button that reveals a short list. The Admin Site's one dropdown mechanism, worn two ways:
 * the cog in the masthead's corner, and an area in the masthead's nav.
 *
 * A disclosure, not an ARIA menu. The menu-button pattern owes its user arrow-key roving focus,
 * type-ahead and `role="menuitem"` semantics that suppress the links underneath; a disclosure is
 * a button with `aria-expanded` over ordinary links, so Tab reaches them, a screen reader still
 * calls them links, and there is no keyboard contract to get subtly wrong. WAI-ARIA's own
 * navigation-menu guidance says as much, and this is navigation.
 *
 * That reasoning is why an area's sub-pages are ONE panel with headings rather than a flyout
 * inside a flyout: a disclosure nests badly, and every reference we looked at renders three
 * levels flat anyway (Pinterest, Fiverr, Hers, Artlist, lululemon, Skillshare).
 *
 * What it does owe the user, and does: Escape closes it and puts focus back on the button, a
 * click anywhere else closes it, and choosing something closes it.
 */
export function Menu({
  label,
  icon,
  text,
  wide,
  align = 'right',
  badge,
  children,
}: {
  label: string
  /** Text mode only: a count riding on the label, and the words that say what it counts. */
  badge?: ReactNode
  /** The corner treatment: an icon button, named only by `label`. */
  icon?: ReactNode
  /** The nav treatment: the label shown as text, wearing the area nav's border. */
  text?: boolean
  /** Groups side by side rather than stacked: for an area whose panel is two short lists. */
  wide?: boolean
  align?: 'right' | 'centre' | 'left'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    function close(event: Event) {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Back to the button: closing a panel while focus is inside it leaves focus on nothing,
      // and a keyboard user is then at the top of the document.
      button.current?.focus()
    }

    // pointerdown rather than click, so the panel is gone before whatever was underneath acts.
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)

    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  return (
    <div ref={container} className="relative">
      {/* Text mode names itself, so aria-label and title would only repeat it -- and a title on a
          control whose name is already visible is a tooltip saying what the user can read. */}
      <button
        ref={button}
        type="button"
        {...(text ? {} : { 'aria-label': label, title: label })}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={
          text
            ? // AreaNav's treatment, kept exactly: the border is always drawn so nothing shifts
              // by 2px, and hover moves toward green as an ordinary area link does.
              'inline-flex min-h-6 items-center gap-1 border-b-2 border-transparent pb-1 text-nav font-bold whitespace-nowrap text-body duration-(--duration-base) ease-(--ease-standard) transition-colors hover:text-green-700 aria-expanded:border-green-700 aria-expanded:text-green-700'
            : 'flex rounded-md p-1.5 text-green-800 hover:bg-green-050'
        }
      >
        {text ? (
          <>
            <span className="relative">
              {label}
              {badge}
            </span>
            <ChevronDown aria-hidden="true" className={`size-4 ${open ? 'rotate-180' : ''}`} />
          </>
        ) : (
          icon
        )}
      </button>

      {open && (
        <div
          // Closing on the way out covers every item at once: a link that navigates and a button
          // that signs out both leave the panel behind otherwise.
          onClick={() => setOpen(false)}
          className={
            'absolute top-full z-10 mt-2 min-w-56 rounded-lg border border-ink-100 bg-card p-2 ' +
            // The cog sits in the corner, so its panel hangs from the right edge or it would run
            // off the page. An area sits in a centred row, so its panel centres on the trigger.
            // The Exam switcher sits in the other corner, so its panel hangs from the left.
            (align === 'centre' ? 'left-1/2 -translate-x-1/2' : align === 'left' ? 'left-0' : 'right-0')
          }
        >
          {/* Wide puts the groups side by side: two short lists read faster as two columns than
              as one list twice as tall, and the panel is already as wide as its longest row.
              items-start so a shorter column stops at its last row rather than stretching to
              match its neighbour and hanging a hover target under nothing. */}
          <ul
            aria-label={label}
            className={wide ? 'flex flex-row items-start gap-4' : 'flex flex-col'}
          >
            {children}
          </ul>
        </div>
      )}
    </div>
  )
}

/** One row of a menu: a link, a button, or a line of text that is neither. */
export function MenuRow({ children }: { children: ReactNode }) {
  return <li className="contents">{children}</li>
}

/**
 * A named run of rows, so one panel can carry an area's sub-pages under headings instead of
 * opening a second panel per heading.
 *
 * A box rather than `contents`, which is what lets `wide` work without the group being told
 * about it: stacked, a full-width group renders exactly as its heading and rows did loose in
 * the panel; in a row, the same box is a column. MenuRow stays `contents`, because a row is
 * never the thing being laid out.
 */
export function MenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li className="flex flex-col">
      <p className="px-3 pt-2 pb-1 text-xs font-bold tracking-widest text-muted uppercase">
        {label}
      </p>
      <ul aria-label={label} className="flex flex-col">
        {children}
      </ul>
    </li>
  )
}

/** The class every actionable row wears, so a link and a button read as one list. */
export const menuItem =
  'rounded-md px-3 py-2 text-left hover:bg-green-050 hover:text-green-800'
